import StatusBadge from '@/components/base/StatusBadge';
import { formatMoney } from '@/utils/format';
import { channelLabel } from '@/utils/bulkPayments';
import type { BulkTransfer } from '@/types/portal';

interface BulkTransferHistoryProps {
  transfers: BulkTransfer[];
  onReschedule: (transfer: BulkTransfer) => void;
  onCancel: (transfer: BulkTransfer) => void;
}

export default function BulkTransferHistory({
  transfers,
  onReschedule,
  onCancel,
}: BulkTransferHistoryProps) {
  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            Recent bulk transfers
          </h3>
          <p className="mt-0.5 text-xs text-foreground-500">
            Submitted transfers and settlement status
          </p>
        </div>
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-200 text-base text-foreground-600">
          <i className="ri-history-line" />
        </span>
      </header>

      <ul className="divide-y divide-background-100">
        {transfers.slice(0, 5).map((transfer) => {
          const isScheduled = transfer.status === 'Scheduled';
          return (
            <li key={transfer.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground-950">{transfer.reference}</p>
                <p className="text-xs text-foreground-500">
                  {channelLabel(transfer.channel)} · {transfer.recipientCount} recipient
                  {transfer.recipientCount === 1 ? '' : 's'} · {transfer.createdAt} ·{' '}
                  {transfer.createdBy}
                </p>
                {transfer.scheduledFor ? (
                  <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-secondary-800">
                    <i className="ri-calendar-schedule-line" />
                    Goes out {transfer.scheduledFor}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-sm text-foreground-800">
                  {formatMoney(transfer.total, transfer.currency)}
                </span>
                <StatusBadge status={transfer.status} />
                {isScheduled ? (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      title="Reschedule this batch"
                      aria-label={`Reschedule ${transfer.reference}`}
                      onClick={() => onReschedule(transfer)}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-sm text-foreground-600 transition-colors hover:border-secondary-300 hover:text-secondary-900"
                    >
                      <i className="ri-calendar-schedule-line" />
                    </button>
                    <button
                      type="button"
                      title="Cancel this batch"
                      aria-label={`Cancel ${transfer.reference}`}
                      onClick={() => onCancel(transfer)}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                    >
                      <i className="ri-close-circle-line" />
                    </button>
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}